import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import type { EChartsOption } from "echarts";

export type ChartSize = { width: number; height: number };
export type ChartOptionInput = EChartsOption | ((size: ChartSize) => EChartsOption);

function resolveOption(option: ChartOptionInput, el: HTMLElement): EChartsOption {
  if (typeof option === "function") {
    return option({ width: el.clientWidth, height: el.clientHeight });
  }
  return option;
}

/** Mounts one ECharts instance per container and keeps it in sync with
 *  `option`. A ResizeObserver on the container handles both window resizes
 *  and the sidebar-collapse width transition — no manual setTimeout needed,
 *  unlike the design prototype's approach.
 *
 *  `option` may be a function of the container size so charts that need a
 *  measured layout (the due-by-type pie) can hug the available box. */
export function useEChart(option: ChartOptionInput | null) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<echarts.ECharts | null>(null);
  const optionRef = useRef(option);
  optionRef.current = option;

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const instance = echarts.init(el, null, { renderer: "canvas" });
    instanceRef.current = instance;

    const apply = () => {
      const current = optionRef.current;
      if (!current) return;
      instance.setOption(resolveOption(current, el), true);
    };

    const observer = new ResizeObserver(() => {
      instance.resize();
      apply();
    });
    observer.observe(el);
    apply();

    return () => {
      observer.disconnect();
      instance.dispose();
      instanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    const instance = instanceRef.current;
    const el = containerRef.current;
    if (!instance || !el || !option) return;
    instance.setOption(resolveOption(option, el), true);
  }, [option]);

  return containerRef;
}
